import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { QuestionWithCircle } from "components/icons/QuestionWithCircle";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useState, useMemo, useCallback, useEffect } from "react";
import Popover from "components/DesignSystem/Popover/Popover";
import dayjs from "dayjs";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import {
  AI_RECONCILE_TXN_ACTION,
  AI_RECONCILE_MISSING_TXN_ACTION,
  AI_RECONCILE_SECTION_TYPE,
  AIReconcileTxnActionType,
  AIReconcileMissingActionType,
} from "constants/dataSource";
import {
  useCompleteReconciliationMutation,
  useGetAIReconcileDetailQuery,
} from "store/apis/reconciliation";
import { ReconciliationConsole } from "components/Reconciliation/ReconciliationConsole";
import { AIReconcileAccordionSection } from "./AIReconcileAccordionSection";
import { AIReconcileManualAdditions } from "./AIReconcileManualAdditions";
import { Transactions } from "types/Models/books";
import { ReconciliationHeader } from "components/Reconciliation/ReconciliationTxn";

type ManualTransactionRow = {
  id: string;
  date: string;
  amount: number;
  description: string;
  merchant: string;
  category: string;
  invoice: unknown;
};

export const AIReconcileDetailModal = ({
  isOpen,
  onClose,
  entityId: propEntityId,
  reconciliationStateId,
}: {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  reconciliationStateId: string;
}) => {
  const entityId = useCurrentEntityId() || propEntityId;
  const { uuid: groupId } = useCurrentChatGroupContext();
  const { alertToast, successToast } = useToast();

  const [txnActions, setTxnActions] = useState<
    Record<string, AIReconcileTxnActionType>
  >({});
  const [missingTxnActions, setMissingTxnActions] = useState<
    Record<number, AIReconcileMissingActionType>
  >({});
  const [manualAdditions, setManualAdditions] = useState<
    ManualTransactionRow[]
  >([]);

  const { data, isLoading, isFetching, isError } = useGetAIReconcileDetailQuery(
    { entityId, reconciliationStateId },
    { skip: !entityId || !reconciliationStateId }
  );

  useEffect(() => {
    if (isError) {
      alertToast({
        message: "Couldn't load this reconciliation. Please try again.",
      });
      onClose();
    }
  }, [isError, alertToast, onClose]);

  const [completeAIReconciliation, { isLoading: confirmLoading }] =
    useCompleteReconciliationMutation();

  const handleTxnActionChange = useCallback(
    (txnId: string, action: AIReconcileTxnActionType) => {
      setTxnActions((prev) => ({ ...prev, [txnId]: action }));
    },
    []
  );

  const handleMissingTxnActionChange = useCallback(
    (index: number, action: AIReconcileMissingActionType) => {
      setMissingTxnActions((prev) => ({ ...prev, [index]: action }));
    },
    []
  );

  const getDefaultAction = useCallback(
    (txn: Transactions): AIReconcileTxnActionType => {
      if (txn.excluded_from_reconciliation) {
        return AI_RECONCILE_TXN_ACTION.EXCLUDE;
      }
      if (txn.is_reconciled) {
        return AI_RECONCILE_TXN_ACTION.RECONCILE;
      }
      return AI_RECONCILE_TXN_ACTION.DO_NOT_RECONCILE;
    },
    []
  );

  const allBooksTxns: Transactions[] = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.auto_reconciled || []).map((p) => p.books_txn).filter(Boolean),
      ...(data.possible_matches || []).map((p) => p.books_txn).filter(Boolean),
      ...(data.potential_duplicates || []),
      ...(data.still_unreconciled || []),
    ] as Transactions[];
  }, [data]);

  // Compute IDs for reconcile/unreconcile/exclude based on actions
  const { txnIdsToReconcile, unreconciledTxnIds, excludedTxnIds } =
    useMemo(() => {
      const reconcileIds: string[] = [];
      const unreconcileIds: string[] = [];
      const excludeIds: string[] = [];

      allBooksTxns.forEach((txn) => {
        const txnId = txn.transaction.uuid;
        const action = txnActions[txnId] || getDefaultAction(txn);

        if (action === AI_RECONCILE_TXN_ACTION.EXCLUDE) {
          if (!txn.excluded_from_reconciliation) {
            excludeIds.push(txnId);
          }
        } else if (action === AI_RECONCILE_TXN_ACTION.RECONCILE) {
          if (!txn.is_reconciled) {
            reconcileIds.push(txnId);
          }
        } else if (action === AI_RECONCILE_TXN_ACTION.DO_NOT_RECONCILE) {
          if (txn.is_reconciled) {
            unreconcileIds.push(txnId);
          }
        }
      });

      return {
        txnIdsToReconcile: reconcileIds,
        unreconciledTxnIds: unreconcileIds,
        excludedTxnIds: excludeIds,
      };
    }, [txnActions, allBooksTxns, getDefaultAction]);

  const createAndReconcileTxns = useMemo(() => {
    if (!data?.missing_in_books) return [];

    return data.missing_in_books
      .filter((_, index) => {
        const action = missingTxnActions[index];
        return (
          action === undefined ||
          action === AI_RECONCILE_MISSING_TXN_ACTION.CREATE_AND_RECONCILE
        );
      })
      .map((txn) => ({
        date: txn.date,
        description: txn.description,
        amount: txn.amount,
      }));
  }, [data?.missing_in_books, missingTxnActions]);

  const { adjustedMoneyIn, adjustedMoneyOut, inkleBooksBalance, difference } =
    useMemo(() => {
      if (!data)
        return {
          adjustedMoneyIn: 0,
          adjustedMoneyOut: 0,
          inkleBooksBalance: 0,
          difference: 0,
        };

      let adjustedIn = data.money_in || 0;
      let adjustedOut = data.money_out || 0;

      [...unreconciledTxnIds, ...excludedTxnIds].forEach((txnId) => {
        const txn = allBooksTxns.find((t) => t.transaction.uuid === txnId);
        if (!txn) return;

        // Only adjust balance for txns that were originally reconciled.
        // Switching between "do not reconcile" and "exclude" should not
        // affect the calculation since neither contributes to the balance.
        if (!txn.is_reconciled) return;

        const amount = txn.transaction.amount || 0;
        if (amount < 0) {
          adjustedOut = adjustedOut - amount;
        } else {
          adjustedIn = adjustedIn - amount;
        }
      });

      txnIdsToReconcile.forEach((txnId) => {
        const txn = allBooksTxns.find((t) => t.transaction.uuid === txnId);
        if (!txn) return;
        const amount = txn.transaction.amount || 0;
        if (amount < 0) {
          adjustedOut = adjustedOut + amount;
        } else {
          adjustedIn = adjustedIn + amount;
        }
      });

      createAndReconcileTxns.forEach((txn) => {
        if (txn.amount < 0) {
          adjustedOut = adjustedOut + txn.amount;
        } else {
          adjustedIn = adjustedIn + txn.amount;
        }
      });

      manualAdditions.forEach((txn) => {
        if (!txn.date || !txn.description || !txn.amount) return;
        if (txn.amount < 0) {
          adjustedOut = adjustedOut + txn.amount;
        } else {
          adjustedIn = adjustedIn + txn.amount;
        }
      });

      const parsedIn = parseFloat(adjustedIn.toFixed(2));
      const parsedOut = parseFloat(adjustedOut.toFixed(2));
      const balance = parseFloat(
        ((data.as_of_start_date_balance || 0) + parsedIn + parsedOut).toFixed(2)
      );
      const diff = parseFloat(
        ((data.statement_ending_balance || 0) - balance).toFixed(2)
      );

      return {
        adjustedMoneyIn: parsedIn,
        adjustedMoneyOut: parsedOut,
        inkleBooksBalance: balance,
        difference: diff,
      };
    }, [
      data,
      allBooksTxns,
      unreconciledTxnIds,
      excludedTxnIds,
      txnIdsToReconcile,
      createAndReconcileTxns,
      manualAdditions,
    ]);

  const differenceZero = difference === 0;

  const confirmModal = useModal();
  const exitModal = useModal();

  // Compute summary counts for confirm modal
  const confirmSummary = useMemo(() => {
    if (!data)
      return { reconciled: 0, excluded: 0, unreconciled: 0, created: 0 };

    let reconciled = 0;
    let excluded = 0;
    let unreconciled = 0;

    const allTxns = [
      ...(data.auto_reconciled || []).map((p) => p.books_txn).filter(Boolean),
      ...(data.possible_matches || []).map((p) => p.books_txn).filter(Boolean),
      ...(data.potential_duplicates || []),
      ...(data.still_unreconciled || []),
    ] as Transactions[];

    allTxns.forEach((txn) => {
      const action = txnActions[txn.transaction.uuid] || getDefaultAction(txn);
      if (action === AI_RECONCILE_TXN_ACTION.EXCLUDE) {
        excluded++;
      } else if (action === AI_RECONCILE_TXN_ACTION.DO_NOT_RECONCILE) {
        unreconciled++;
      } else {
        reconciled++;
      }
    });

    const created = (data.missing_in_books || []).filter(
      (_, i) =>
        missingTxnActions[i] ===
          AI_RECONCILE_MISSING_TXN_ACTION.CREATE_AND_RECONCILE ||
        missingTxnActions[i] === undefined
    ).length;

    const validManualAdditions = manualAdditions.filter(
      (txn) => txn.date && txn.description
    ).length;

    return {
      reconciled,
      excluded,
      unreconciled,
      created: created + validManualAdditions,
    };
  }, [data, txnActions, missingTxnActions, manualAdditions, getDefaultAction]);

  const onConfirm = async () => {
    if (!data) return;

    const manualAdditionsPayload = manualAdditions
      .filter((txn) => txn.date && txn.description)
      .map((txn) => ({
        date: dayjs(txn.date).format(YYYY_MM_DD),
        amount: txn.amount,
        description: txn.description,
        merchant_data_id: txn.merchant || undefined,
        transaction_category_id: txn.category || undefined,
        is_added_from_reconciliation_table: true,
      }));

    const manualTransactions = [
      ...createAndReconcileTxns,
      ...manualAdditionsPayload,
    ];

    try {
      await completeAIReconciliation({
        groupId,
        entityId,
        reconcilationId: reconciliationStateId,
        txn_ids_to_reconcile: txnIdsToReconcile,
        unreconciled_transaction_ids: unreconciledTxnIds,
        ...(excludedTxnIds.length > 0 && {
          excluded_transaction_ids: excludedTxnIds,
        }),
        ...(manualTransactions.length > 0 && {
          manual_transactions: manualTransactions,
        }),
      }).unwrap();

      confirmModal.close();
      successToast({
        title: "Reconciliation completed successfully",
        message: `For ${data.bank_name} till ${dayjs(
          data.reconciliation_end_date
        ).format(DISPLAY_DATE)}`,
      });
      onClose();
    } catch (error: any) {
      confirmModal.close();
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const handleExit = () => {
    exitModal.close();
    onClose();
  };

  if (isLoading || isFetching || !data) {
    return (
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content size="fullscreen" useCustomOverlay>
          <Modal.Header className="t-mx-8 !t-px-0">
            <div>
              <Modal.Title>Reconcile</Modal.Title>
            </div>
            <Modal.Close type="button" />
          </Modal.Header>
          <Modal.Body className="t-flex-1">
            <Loader />
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    );
  }

  const reconcilTxnData = {
    as_of_start_date_balance: data.as_of_start_date_balance,
    bank_csv_id: data.bank_csv_id,
    entity_bank_account_id: data.entity_bank_account_id,
    inkle_total_balance: data.inkle_total_balance,
    last_reconcilation_status: data.last_reconcilation_status,
    money_in: data.money_in,
    money_out: data.money_out,
    reconciliation_end_date: data.reconciliation_end_date,
    reconciliation_start_date: data.reconciliation_start_date,
    statement_ending_balance: data.statement_ending_balance,
    statement_starting_balance: data.statement_starting_balance,
    transactions: [],
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={exitModal.open}>
      <Modal.Content size="fullscreen" useCustomOverlay>
        <Modal.Header className="t-mx-8 !t-px-0">
          <ReconciliationHeader
            brandName={data.bank_name ?? ""}
            accountName={data.account_name ?? ""}
          />
        </Modal.Header>
        <Modal.Body className="t-flex-1">
          <div className="t-flex t-flex-col t-gap-4">
            <div className="t-text-body-sm t-text-text-30">
              Inkle AI reviewed your statement and transactions and suggested
              the best updates for reconciliation.
              <br />
              Please review the suggestions below; add or modify as needed and
              confirm.
            </div>

            <ReconciliationConsole
              reconcilTxn={reconcilTxnData}
              inkleBooksBalance={inkleBooksBalance}
              difference={difference}
              adjustedMoneyIn={adjustedMoneyIn}
              adjustedMoneyOut={adjustedMoneyOut}
            />

            <div className="t-flex t-flex-col t-gap-3">
              <AIReconcileAccordionSection
                title="transactions identified as exact matches"
                subtitle="These transactions are a complete match in both the bank statement and Inkle Books."
                sectionType={AI_RECONCILE_SECTION_TYPE.PAIRED}
                pairedTransactions={data.auto_reconciled}
                txnActions={txnActions}
                onTxnActionChange={handleTxnActionChange}
                getDefaultAction={getDefaultAction}
              />

              <AIReconcileAccordionSection
                title="transactions identified as partial matches"
                subtitle="Amounts match, but the dates don't — needs your attention before reconciling."
                sectionType={AI_RECONCILE_SECTION_TYPE.PAIRED}
                pairedTransactions={data.possible_matches}
                txnActions={txnActions}
                onTxnActionChange={handleTxnActionChange}
                getDefaultAction={getDefaultAction}
              />

              <AIReconcileAccordionSection
                title="transactions identified as missing in Inkle Books"
                subtitle="These transactions are in the bank statement but not in Inkle books. Hence, will be created and added to Inkle Books accordingly."
                sectionType={AI_RECONCILE_SECTION_TYPE.MISSING_IN_BOOKS}
                missingTransactions={data.missing_in_books}
                missingTxnActions={missingTxnActions}
                onMissingTxnActionChange={handleMissingTxnActionChange}
              />

              <AIReconcileAccordionSection
                title="transactions flagged as duplicates"
                subtitle="These transactions are recorded more than once in Inkle Books."
                sectionType={AI_RECONCILE_SECTION_TYPE.PAIRED}
                pairedTransactions={data.potential_duplicates.map((txn) => ({
                  statement_txn: null,
                  books_txn: txn,
                }))}
                txnActions={txnActions}
                onTxnActionChange={handleTxnActionChange}
                getDefaultAction={getDefaultAction}
                emptyMessage="No transaction found since the transactions on Inkle Books appear to be duplicates"
              />

              <AIReconcileAccordionSection
                title="transactions flagged as extra in Inkle Books"
                subtitle="No matching entry found in the bank statement — suggested to skip for this reconciliation."
                sectionType={AI_RECONCILE_SECTION_TYPE.PAIRED}
                pairedTransactions={data.still_unreconciled.map((txn) => ({
                  statement_txn: null,
                  books_txn: txn,
                }))}
                txnActions={txnActions}
                onTxnActionChange={handleTxnActionChange}
                getDefaultAction={getDefaultAction}
                emptyMessage="No transaction found the statement that matches with these"
              />

              <AIReconcileManualAdditions
                onTransactionsChange={setManualAdditions}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="t-flex t-w-full t-justify-between t-items-center">
            <SeeHowItWorks />
            <div className="t-flex t-gap-3">
              <Button customType="secondary" onClick={exitModal.open}>
                Cancel
              </Button>
              <Button
                customType="primary"
                onClick={confirmModal.open}
                disabled={!differenceZero || confirmLoading}
                isLoading={confirmLoading}
              >
                Confirm reconciliation
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Content>

      <ConfirmReconciliationModal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.close}
        onConfirm={onConfirm}
        isLoading={confirmLoading}
        summary={confirmSummary}
      />

      <ExitReconciliationModal
        isOpen={exitModal.isOpen}
        onClose={exitModal.close}
        onExit={handleExit}
      />
    </Modal.Root>
  );
};

export const SeeHowItWorks = () => {
  const [cardVisible, setCardVisible] = useState(false);

  return (
    <Popover.Root open={cardVisible} onOpenChange={setCardVisible}>
      <Popover.Trigger asChild>
        <Button size="small" customType="ghost">
          <div className="t-flex t-gap-1 t-items-center">
            <span className="t-text-text-100">See how it works</span>
            <QuestionWithCircle />
          </div>
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="end"
          className="t-z-popover t-w-96 t-shadow-light-60 t-bg-surface t-rounded-lg t-p-6 t-pt-5"
        >
          <Popover.Arrow className="t-fill-white" />
          <div className="t-flex t-flex-col !t-text-start t-gap-3 t-text-body-sm t-text-text-30">
            <div>
              Inkle AI compares your Books transactions with your bank statement
              and grouped them to make review easier.
            </div>
            <div>
              <div className="t-mb-2">Here’s what the buckets mean:</div>
              <ul className="t-list-disc t-pl-5 t-flex t-flex-col t-gap-1">
                <li>
                  <span className="t-text-subtext-sm t-text-text-100">
                    Fully Matched:
                  </span>{" "}
                  Exact match between Books and statement. AI suggests
                  reconciling them.
                </li>
                <li>
                  <span className="t-text-subtext-sm t-text-text-100">
                    Partially Matched:
                  </span>{" "}
                  Details match but date differs. AI suggests reviewing before
                  reconciling.
                </li>
                <li>
                  <span className="t-text-subtext-sm t-text-text-100">
                    Missing in Books:
                  </span>{" "}
                  Found in statement but not in Books. AI suggests creating &
                  reconciling these transactions.
                </li>
                <li>
                  <span className="t-text-subtext-sm t-text-text-100">
                    Potential Duplicates:
                  </span>{" "}
                  Likely duplicate entries in Books. AI suggests excluding them.
                </li>
                <li>
                  <span className="t-text-subtext-sm t-text-text-100">
                    Still Unreconciled:
                  </span>{" "}
                  No match found in the statement. AI suggests leaving them
                  unreconciled for now.
                </li>
              </ul>
            </div>
            <div>
              You can review each bucket, override the suggested action, or
              create/update transactions manually before finalising
              reconciliation.
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const ConfirmReconciliationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  summary,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  summary: {
    reconciled: number;
    excluded: number;
    unreconciled: number;
    created: number;
  };
}) => {
  const summaryItems = [
    { count: summary.reconciled, label: "transactions will be reconciled" },
    {
      count: summary.excluded,
      label: "transactions will be excluded as duplicates",
    },
    {
      count: summary.unreconciled,
      label: "transactions will remain unreconciled",
    },
    {
      count: summary.created,
      label: "new transactions will be created in Books and reconciled",
    },
  ].filter((item) => item.count > 0);

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="small">
        <StatusModal
          type="warning"
          title="Are you sure you want to confirm?"
          showCloseButton={false}
        >
          <div className="t-flex t-flex-col t-gap-2 t-mt-2 t-text-body-sm">
            <div>On confirmation, we'll apply these actions automatically:</div>
            <ul className="t-list-disc t-pl-5 t-flex t-flex-col t-gap-1">
              {summaryItems.map((item) => (
                <li key={item.label}>
                  {item.count} {item.label}
                </li>
              ))}
            </ul>
          </div>
        </StatusModal>
        <Modal.Footer className="t-flex t-justify-end t-gap-3">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            customType="primary"
            onClick={onConfirm}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};

const ExitReconciliationModal = ({
  isOpen,
  onClose,
  onExit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}) => {
  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content size="small">
        <StatusModal
          type="danger"
          title="Exit without completing reconciliation?"
          showCloseButton={false}
        >
          <div className="t-mt-2 t-text-body-sm">
            Leaving now will discard any changes made and this reconciliation
            will be incomplete.
          </div>
        </StatusModal>
        <Modal.Footer className="t-flex t-justify-end t-gap-3">
          <Button onClick={onClose}>Cancel</Button>
          <Button customType="danger" onClick={onExit}>
            Yes, exit
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
